import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { API_ENDPOINTS, AUTH_CRED } from './httpConstants';
import { AuthClient } from './authClient';
const Axios: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_NEXT_PUBLIC_REST_API_ENDPOINT,
  timeout: 50000,
  headers: {},
});

Axios.interceptors.request.use((config: any) => {
  const tokenStr = localStorage.getItem(AUTH_CRED);
  let token = '';

  if (tokenStr) {
    try {
      const parsed = JSON.parse(tokenStr) as { token?: string; refreshToken?: string };
      token = parsed.token ?? '';
    } catch {
      token = '';
    }
  }

  config.headers = {
    ...(config.headers || {}),
    Authorization: `Bearer ${token}`,
  };

  return config;
});

Axios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== API_ENDPOINTS.AUTH.LOGIN
    ) {
      originalRequest._retry = true;
      const credentials = HttpClient.getAuthCredentials();
      if (credentials?.refreshToken) {
        try {
          const { accessToken, refreshToken } = await AuthClient.refresh({
            refreshToken: credentials.refreshToken,
          });
          HttpClient.setAuthCredentials(accessToken, refreshToken);
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return Axios(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem(AUTH_CRED);
          window.location.href = '/login'; // Redirect to login
          return Promise.reject(refreshError);
        }
      }
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem(AUTH_CRED);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

export class HttpClient {
  static async get<T = any>(
    url: string,
    config: { params?: Record<string, any> } = {},
  ): Promise<T> {
    const response = await Axios.get<T>(url, config);
    return response.data;
  }

  static async post<T = any>(url: string, data?: any, options?: AxiosRequestConfig): Promise<T> {
    const response = await Axios.post<T>(url, data, options);
    return response.data;
  }

  static async put<T = any>(url: string, data?: any): Promise<T> {
    const response = await Axios.put<T>(url, data);
    return response.data;
  }

  static async delete<T = any>(url: string): Promise<T> {
    const response = await Axios.delete<T>(url);
    return response.data;
  }

  static getAuthCredentials(): { token: string; refreshToken: string } | null {
    const tokenStr = localStorage.getItem(AUTH_CRED);
    if (tokenStr) {
      try {
        const parsed = JSON.parse(tokenStr) as { token?: string; refreshToken?: string };
        return { token: parsed.token ?? '', refreshToken: parsed.refreshToken ?? '' };
      } catch {
        return null;
      }
    }
    return null;
  }

  static setAuthCredentials(token: string, refreshToken: string): void {
    localStorage.setItem(AUTH_CRED, JSON.stringify({ token, refreshToken }));
  }
}

export function getFormErrors(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.message ?? null;
  }
  return null;
}

export function getFieldErrors(error: unknown): any | null {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.errors ?? null;
  }
  return null;
}
